/**
 * Ladnini — in-place filtering/navigation for the "Product grid" section
 * (sections/product-grid.liquid + snippets/ladnini-filter-sidebar.liquid).
 * ==========================================================================
 * Two independent jobs:
 *
 * 1. Price slider visuals: keeps the two range-input handles from crossing,
 *    paints the fill bar between them, and live-updates the "X zł — Y zł"
 *    labels while dragging.
 *
 * 2. In-place navigation: category links, the sort dropdown, and the
 *    price/size filter form normally cause a full page reload. This
 *    intercepts those clicks/submits and instead fetches just this
 *    section's re-rendered HTML via Shopify's section-rendering endpoint
 *    (?section_id=...), swaps it in, and updates the URL with
 *    history.pushState — so clicking a category filters/replaces the grid
 *    on the same page instead of navigating away.
 *
 *    Only elements marked [data-ladnini-ajax] are intercepted — that's the
 *    sidebar (category tree, price/size filter form, "Wyczyść filtry") and
 *    the sort toolbar. Everything else inside .ladnini-pg-layout — product
 *    card links, "Add to cart" buttons, pagination — is deliberately left
 *    alone and behaves like normal links/forms. An earlier version
 *    intercepted every link/form in the whole layout, which also caught
 *    product card clicks and silently broke navigating to a product.
 *
 * Degrades safely: if fetch fails, or the response doesn't contain the
 * expected markup, it falls back to a normal full navigation.
 */
(function () {
  function formatMoney(value) {
    var rounded = Math.round(value);
    return rounded.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + ' zł';
  }

  function initPriceSliders(root) {
    var sliders = root.querySelectorAll('[data-price-slider]');
    sliders.forEach(function (slider) {
      if (slider.dataset.ladniniBound) return;
      slider.dataset.ladniniBound = 'true';

      var minInput = slider.querySelector('[data-price-input="min"]');
      var maxInput = slider.querySelector('[data-price-input="max"]');
      var fill = slider.querySelector('[data-price-fill]');
      var priceBlock = slider.closest('.ladnini-fsb__price');
      var minLabel = priceBlock && priceBlock.querySelector('[data-price-min-label]');
      var maxLabel = priceBlock && priceBlock.querySelector('[data-price-max-label]');
      var boundMin = parseFloat(slider.dataset.min);
      var boundMax = parseFloat(slider.dataset.max);
      if (!minInput || !maxInput || !fill || isNaN(boundMin) || isNaN(boundMax) || boundMax <= boundMin) return;

      function update() {
        var minVal = parseFloat(minInput.value);
        var maxVal = parseFloat(maxInput.value);
        if (minVal > maxVal) {
          if (document.activeElement === maxInput) {
            minVal = maxVal;
            minInput.value = String(minVal);
          } else {
            maxVal = minVal;
            maxInput.value = String(maxVal);
          }
        }
        var range = boundMax - boundMin;
        var pctMin = ((minVal - boundMin) / range) * 100;
        var pctMax = ((maxVal - boundMin) / range) * 100;
        fill.style.left = pctMin + '%';
        fill.style.right = (100 - pctMax) + '%';
        if (minLabel) minLabel.textContent = formatMoney(minVal);
        if (maxLabel) maxLabel.textContent = formatMoney(maxVal);
      }

      minInput.addEventListener('input', update);
      maxInput.addEventListener('input', update);
      update();
    });
  }

  function initAjaxNav(layout) {
    if (layout.dataset.ladniniAjaxBound) return;
    layout.dataset.ladniniAjaxBound = 'true';

    var sectionId = layout.dataset.sectionId;

    function setLoading(isLoading) {
      layout.classList.toggle('is-loading', isLoading);
    }

    function swap(html, url) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var newLayout = doc.querySelector('.ladnini-pg-layout');
      if (!newLayout) {
        window.location.assign(url);
        return;
      }
      layout.innerHTML = newLayout.innerHTML;
      layout.dataset.sectionId = newLayout.dataset.sectionId || sectionId;
      window.history.pushState({ ladniniPg: true }, '', url);
      initPriceSliders(layout);
      layout.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    function go(url) {
      if (!sectionId) {
        window.location.assign(url);
        return;
      }
      setLoading(true);
      var sep = url.indexOf('?') === -1 ? '?' : '&';
      fetch(url + sep + 'section_id=' + encodeURIComponent(sectionId))
        .then(function (res) {
          if (!res.ok) throw new Error('Ladnini filter fetch failed: ' + res.status);
          return res.text();
        })
        .then(function (html) {
          swap(html, url);
        })
        .catch(function () {
          window.location.assign(url);
        })
        .finally(function () {
          setLoading(false);
        });
    }

    layout.addEventListener('click', function (event) {
      var link = event.target.closest('a[href]');
      if (!link || !layout.contains(link)) return;
      if (!link.closest('[data-ladnini-ajax]')) return;
      if (link.target === '_blank' || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
      var href = link.getAttribute('href');
      if (!href || href.charAt(0) === '#') return;
      event.preventDefault();
      go(link.href);
    });

    layout.addEventListener('submit', function (event) {
      var form = event.target;
      if (!form || form.tagName !== 'FORM' || !layout.contains(form)) return;
      if (!form.closest('[data-ladnini-ajax]')) return;
      event.preventDefault();
      var params = new URLSearchParams(new FormData(form));
      var action = form.getAttribute('action') || window.location.pathname;
      var query = params.toString();
      go(action + (query ? '?' + query : ''));
    });

    window.addEventListener('popstate', function () {
      go(window.location.href);
    });
  }

  function init() {
    var layout = document.querySelector('.ladnini-pg-layout');
    if (layout) {
      initAjaxNav(layout);
      initPriceSliders(layout);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();